/**
 * Blueprint and version reads (T2.12).
 *
 * The payload shapes here were agreed with the Backend agent before either side
 * was written. Two of them are deliberate, not incidental:
 *
 *   1. `model.raw` is required, not optional. `buildModel` stamps `__i` on every
 *      deliverable row, and both the week highlighter and the Phase 3 click
 *      handler key off that index. A payload that drops `raw` breaks both.
 *
 *   2. `schedule`, `holidays` and `leave` are siblings of `model`, never
 *      attached onto it. CLAUDE.md records a generator bug where a rebuild
 *      copied schedule and holidays but dropped leave; keeping the three beside
 *      the model, in one type, makes that class of mistake structurally
 *      impossible rather than merely avoided.
 *
 * Nothing in this module recomputes a percentage. `BlueprintModel` arrives
 * already computed by the shared `buildModel` the API also uses; the browser
 * only ever calls that same function, never its own arithmetic.
 */
import { useQuery } from '@tanstack/react-query';
import type { BlueprintModel, Terminology, TerminologyOverride } from '@blueprint/shared';
import { api } from './api';

/** design.md § 5. Canonical keys — terminology overrides never touch these. */
export type BlueprintRole = 'admin' | 'editor' | 'viewer';

/**
 * design.md § 2: "Published" is both a deliverable state and a version status.
 * They are never the same type. In the interface, versions read as Draft and
 * Live — a stated convention, not an incidental choice.
 */
export type VersionStatus = 'draft' | 'published';

export interface ClientRef {
  id: string;
  name: string;
  slug: string;
  /**
   * The client's own terminology override — RAW and unresolved, not a resolved
   * `Terminology`. The picker calls `resolveTerms(client.terminology, null)`
   * itself so system defaults fill any key the client has not set.
   *
   * It exists because the picker lists several clients at once, and each group
   * must be described in its own client's words. The API always sends it, using
   * `{}` where the client has set nothing — never null, never absent — so there
   * is only ever one case to handle. It stays optional here so an older response
   * degrades to the system defaults rather than throwing.
   *
   * The API re-parses this through `terminologyOverrideSchema` on the way out,
   * so a value written straight into the column cannot arrive with markup in it.
   * That is a second line of defence, not a reason to skip escaping: these
   * strings render in almost every view, and React escapes them on render.
   */
  terminology?: TerminologyOverride;
}

/** The wire shape of a blueprint, shared by every endpoint that returns one. */
export interface Blueprint {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  /**
   * This blueprint's OWN stored override — raw, not resolved and not inherited.
   * The admin form reads this and PATCHes it back; `terms` on the payload is the
   * resolved set the rest of the interface renders from. They are different
   * things: saving the resolved set as the override would freeze every inherited
   * word in place.
   */
  terminology: TerminologyOverride;
  isArchived: boolean;
  client: ClientRef;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /**
   * What the caller may do here, as the API's guard decided it. The interface
   * shows and hides controls from this rather than re-deriving permission from
   * the user's global role — there is one authority on access and it is not the
   * browser.
   */
  role: BlueprintRole;
}

export interface BlueprintSummary extends Blueprint {
  latestVersionId: string | null;
  /** Always false for a viewer: a viewer is not told a draft exists (design.md D-2). */
  hasDraft: boolean;
}

export interface VersionSummary {
  id: string;
  blueprintId: string;
  versionNo: number;
  status: VersionStatus;
  isLatest: boolean;
  /** The release note, named `label` on the wire. */
  label: string | null;
  createdBy: string;
  createdAt: string;
  publishedAt: string | null;
}

export interface BlueprintDetail {
  blueprint: Blueprint;
  /** Already resolved through system → client → blueprint, inline (T2.10a). */
  terms: Terminology;
  /** Drafts are absent for a viewer, not present-and-flagged (design.md D-2). */
  versions: VersionSummary[];
}

/* Row shapes mirror the workbook columns verbatim — ONBOARDING.md, "The
   workbook format" — because the schedule resolution below is a direct port of
   the generator's, and a renamed field would silently stop matching. */

export interface ScheduleRow {
  RoomID: string;
  PillarName: string;
  StartDate: string;
  EndDate: string;
  Person: string;
  Lead: string;
}

export interface HolidayRow {
  Date: string;
  Label: string;
}

export interface LeaveRow {
  Person: string;
  StartDate: string;
  EndDate: string;
  Label: string;
}

export interface VersionPayload {
  blueprint: Blueprint;
  version: VersionSummary;
  terms: Terminology;
  model: BlueprintModel;
  /* Siblings of `model`, never attached onto it — see the header note. */
  schedule: ScheduleRow[];
  holidays: HolidayRow[];
  leave: LeaveRow[];
}

/* ------------------------------------------------------------------ queries */

export const blueprintKeys = {
  all: ['blueprints'] as const,
  list: () => [...blueprintKeys.all, 'list'] as const,
  detail: (id: string) => [...blueprintKeys.all, 'detail', id] as const,
  latest: (id: string) => [...blueprintKeys.all, 'latest', id] as const,
  draft: (id: string) => [...blueprintKeys.all, 'draft', id] as const,
  version: (versionId: string) => ['versions', versionId] as const,
};

export function useBlueprints() {
  return useQuery({
    queryKey: blueprintKeys.list(),
    queryFn: async ({ signal }) => {
      // The API wraps the list; the picker only ever wants the rows.
      const body = await api.get<{ blueprints: BlueprintSummary[] }>('/blueprints', signal);
      return body.blueprints;
    },
  });
}

export function useBlueprint(id: string | undefined) {
  return useQuery({
    queryKey: blueprintKeys.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<BlueprintDetail>(`/blueprints/${id}`, signal),
    enabled: Boolean(id),
  });
}

/** Which payload a blueprint screen loads when the address names no version. */
export type PayloadSource =
  | { kind: 'version'; versionId: string }
  | { kind: 'draft' }
  | { kind: 'latest' };

/** The query key for a source, shared with the editing code that patches the cache. */
export function payloadKey(blueprintId: string, source: PayloadSource) {
  if (source.kind === 'version') return blueprintKeys.version(source.versionId);
  if (source.kind === 'draft') return blueprintKeys.draft(blueprintId);
  return blueprintKeys.latest(blueprintId);
}

/**
 * The full model for rendering.
 *
 *   version  a specific version the caller names (linkable, survives a reload)
 *   draft    the editor's working copy, in one round trip (T2.10b)
 *   latest   the newest published version — what a viewer always gets
 *
 * `enabled` lets the caller hold the request until it knows which source it
 * wants: an editor's source depends on whether a draft exists, which only the
 * blueprint's version list says.
 */
export function useVersionPayload(
  blueprintId: string | undefined,
  source: PayloadSource,
  enabled = true,
) {
  const path =
    source.kind === 'version'
      ? `/versions/${source.versionId}`
      : source.kind === 'draft'
        ? `/blueprints/${blueprintId}/versions/draft`
        : `/blueprints/${blueprintId}/versions/latest`;

  return useQuery({
    queryKey: payloadKey(blueprintId ?? '', source),
    queryFn: ({ signal }) => api.get<VersionPayload>(path, signal),
    enabled: Boolean(blueprintId) && enabled,
  });
}

/* ------------------------------------------------------------------ helpers */

/** Group a flat list into clients, both lists alphabetical, for the picker. */
export function groupByClient(
  blueprints: BlueprintSummary[],
): { client: ClientRef; blueprints: BlueprintSummary[] }[] {
  const groups = new Map<string, { client: ClientRef; blueprints: BlueprintSummary[] }>();
  for (const b of blueprints) {
    const group = groups.get(b.client.id);
    if (group) group.blueprints.push(b);
    else groups.set(b.client.id, { client: b.client, blueprints: [b] });
  }
  const compare = (a: string, b: string) => a.localeCompare(b, 'en-GB');
  const out = [...groups.values()].sort((a, b) => compare(a.client.name, b.client.name));
  for (const g of out) g.blueprints.sort((a, b) => compare(a.name, b.name));
  return out;
}

/** True where the role may write. The API still decides; this only hides what would be refused. */
export function canEdit(role: BlueprintRole): boolean {
  return role === 'admin' || role === 'editor';
}
